import { Injectable, Type } from "@angular/core";
import { BehaviorSubject } from "rxjs";

export interface WindowConfig {
  id: number;
  type: string;
  component: Type<any>;
  data?: any;
  minimized?: boolean;
  maximized?: boolean;
  anchored?: "left" | "right" | null;
  state?: any;
  position?: { x: number; y: number };
  wasMaximized?: boolean;
  title?: string;
  width?: number;
  height?: number;
  isModified?: boolean;
  isActive?: boolean;
  lastModified?: Date;
  animating?: boolean;
  animationType?:
    | "minimize"
    | "restore"
    | "maximize"
    | "anchor"
    | "close"
    | "appear";
  autoResize?: boolean;
  minHeight?: number;
  maxHeight?: number;
  contentHeight?: number;
  canMinimize?: boolean;
  canMaximize?: boolean;
}

@Injectable({ providedIn: "root" })
export class WindowManagerService {
  private windowsSubject = new BehaviorSubject<WindowConfig[]>([]);
  windows$ = this.windowsSubject.asObservable();

  get windows(): WindowConfig[] {
    return this.windowsSubject.value;
  }

  open(config: WindowConfig) {
    const existing = this.windows.find(
      (w) => w.id === config.id && w.type === config.type
    );
    if (existing) {
      // Si la fenêtre est minimisée, la restaurer
      if (existing.minimized) {
        setTimeout(() => {
          this.restore(existing);
        }, 100);
      }
      this.select(existing);
      return;
    }
    // Désactiver toutes les autres fenêtres
    const updatedWindows = this.windows.map((w) => ({ ...w, isActive: false }));
    // Dimensions par défaut (ou personnalisées)
    const defaultWidth = config.width ?? 600;
    const defaultHeight = config.height ?? 400;
    // Centrage si position non fournie
    const defaultPosition = config.position ?? {
      x: Math.round((window.innerWidth - defaultWidth) / 2),
      y: Math.round((window.innerHeight - defaultHeight) / 2),
    };
    const newWindow: WindowConfig = {
      ...config,
      minimized: false,
      maximized: false,
      anchored: null,
      width: defaultWidth,
      height: defaultHeight,
      position: defaultPosition,
      isModified: false,
      isActive: true,
      lastModified: new Date(),
      animating: true,
      animationType: "appear",
      autoResize: config.autoResize ?? true,
      minHeight: config.minHeight ?? 200,
      maxHeight: config.maxHeight ?? Math.round(window.innerHeight * 0.8),
      contentHeight: 0,
      canMinimize: config.canMinimize ?? true,
      canMaximize: config.canMaximize ?? true,
    };
    this.windowsSubject.next([...updatedWindows, newWindow]);
    // Retirer l'animation après un délai
    setTimeout(() => {
      this.update(newWindow, { animating: false, animationType: undefined });
    }, 300);
  }

  close(window: WindowConfig) {
    // Animation de fermeture
    this.update(window, { animating: true, animationType: "close" });

    // Sur mobile, fermer immédiatement sans attendre l'animation
    const isMobile =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );

    const closeDelay = isMobile ? 100 : 300;

    setTimeout(() => {
      this.windowsSubject.next(
        this.windows.filter(
          (w) => !(w.id === window.id && w.type === window.type)
        )
      );
    }, closeDelay);
  }

  minimize(window: WindowConfig) {
    // Animation de minimisation
    this.update(window, {
      animating: true,
      animationType: "minimize",
      wasMaximized: window.maximized,
      maximized: false,
      isActive: false,
    });

    setTimeout(() => {
      this.update(window, {
        minimized: true,
        animating: false,
        animationType: undefined,
      });
    }, 300);
  }

  maximize(window: WindowConfig) {
    // Animation de maximisation
    this.update(window, {
      animating: true,
      animationType: "maximize",
      maximized: true,
      minimized: false,
      isActive: true,
    });

    setTimeout(() => {
      this.update(window, {
        animating: false,
        animationType: undefined,
      });
    }, 400);
  }

  restore(window: WindowConfig) {
    // Animation de restauration
    this.update(window, {
      animating: true,
      animationType: "restore",
      minimized: false,
      isActive: true,
    });

    setTimeout(() => {
      // Si la fenêtre était maximisée avant d'être réduite, on la remet en maximisé
      if (window.wasMaximized) {
        this.update(window, {
          maximized: true,
          wasMaximized: false,
          animating: false,
          animationType: undefined,
        });
      } else {
        this.update(window, {
          maximized: false,
          wasMaximized: false,
          animating: false,
          animationType: undefined,
        });
      }
    }, 400);
  }

  anchor(window: WindowConfig, side: "left" | "right") {
    // Animation d'ancrage
    this.update(window, {
      animating: true,
      animationType: "anchor",
      anchored: side,
      isActive: true,
    });

    setTimeout(() => {
      this.update(window, {
        animating: false,
        animationType: undefined,
      });
    }, 400);
  }

  unanchor(window: WindowConfig) {
    this.update(window, { anchored: null });
  }

  select(window: WindowConfig) {
    // Mettre la fenêtre sélectionnée en dernier (au-dessus) et la marquer comme active
    const filtered = this.windows.filter(
      (w) => !(w.id === window.id && w.type === window.type)
    );
    // Désactiver toutes les autres fenêtres
    const updatedWindows = filtered.map((w) => ({ ...w, isActive: false }));
    this.windowsSubject.next([
      ...updatedWindows,
      { ...window, isActive: true },
    ]);
  }

  update(window: WindowConfig, changes: Partial<WindowConfig>) {
    const currentWindows = this.windows;
    const updatedWindows = currentWindows.map((w) => {
      if (w.id === window.id && w.type === window.type) {
        // Vérifier si les changements sont réellement différents
        const hasChanges = Object.keys(changes).some((key) => {
          const changeKey = key as keyof WindowConfig;
          return w[changeKey] !== changes[changeKey];
        });

        if (!hasChanges) {
          return w; // Pas de changement, retourner la même référence
        }

        return { ...w, ...changes, lastModified: new Date() };
      }
      return w;
    });

    // Ne mettre à jour que si il y a des changements
    if (updatedWindows !== currentWindows) {
      this.windowsSubject.next(updatedWindows);
    }
  }

  saveState(window: WindowConfig, state: any) {
    this.update(window, { state });
  }

  getState(window: WindowConfig): any {
    return this.windows.find(
      (w) => w.id === window.id && w.type === window.type
    )?.state;
  }

  markAsModified(window: WindowConfig, isModified: boolean = true) {
    this.update(window, { isModified, lastModified: new Date() });
  }

  markAsSaved(window: WindowConfig) {
    this.update(window, { isModified: false });
  }

  getModifiedWindows(): WindowConfig[] {
    return this.windows.filter((w) => w.isModified);
  }

  getActiveWindow(): WindowConfig | null {
    return this.windows.find((w) => w.isActive) || null;
  }

  // Nouvelles méthodes pour l'auto-resize
  updateContentHeight(window: WindowConfig, contentHeight: number) {
    if (!window.autoResize || window.maximized || window.anchored) {
      return;
    }

    const headerHeight = 48; // Hauteur de la barre de titre
    // const padding = 32; // Padding total (haut + bas)
    const minHeight = window.minHeight ?? 200;
    const maxHeight =
      window.maxHeight ?? Math.round(globalThis.window.innerHeight * 0.9);

    // Calculer la nouvelle hauteur sans padding
    const newHeight = Math.max(
      minHeight,
      Math.min(maxHeight, contentHeight + headerHeight)
    );

    // Mettre à jour seulement si la hauteur a changé significativement
    if (Math.abs((window.height ?? 400) - newHeight) > 10) {
      this.update(window, {
        height: newHeight,
        contentHeight: contentHeight,
      });
    }
  }

  setAutoResize(window: WindowConfig, enabled: boolean) {
    this.update(window, { autoResize: enabled });
  }

  setHeightLimits(
    window: WindowConfig,
    minHeight?: number,
    maxHeight?: number
  ) {
    this.update(window, {
      minHeight: minHeight ?? window.minHeight,
      maxHeight: maxHeight ?? window.maxHeight,
    });
  }
}
